import { useMemo, useRef, useState } from 'react';
import { Search, Dumbbell, Plus, Pencil, Trash2, ImagePlus, X } from 'lucide-react';
import type { Exercise } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Modal, MultiSelect, Select, Textarea, Badge, Skeleton, EmptyState, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useAsync } from '../lib/useAsync';
import {
  createCustomExercise,
  deleteCustomExercise,
  getExercises,
  getExerciseFilters,
  updateCustomExercise,
  uploadGymImage,
} from '../lib/gymApi';

const MUSCLE_GROUPS = ['Chest', 'Back', 'Shoulders', 'Arms', 'Legs', 'Core'];

/** Browse the exercise library — seeded global exercises + this gym's custom ones. */
export function ExerciseLibraryPage() {
  const { data: filters } = useAsync(getExerciseFilters);
  const { data: all, loading, reload } = useAsync(() => getExercises());
  const confirm = useConfirm();

  const [group, setGroup] = useState<string | null>(null);
  const [equipment, setEquipment] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [ownership, setOwnership] = useState<'all' | 'custom'>('all');
  const [editing, setEditing] = useState<Exercise | 'new' | null>(null);
  const [viewing, setViewing] = useState<Exercise | null>(null);

  async function remove(e: Exercise) {
    const ok = await confirm({ title: `Delete ${e.name}?`, description: 'It will be removed from your library.', confirmLabel: 'Delete', tone: 'danger' });
    if (!ok) return;
    await deleteCustomExercise(e.id);
    setViewing(null);
    reload();
  }

  const customCount = useMemo(() => (all ?? []).filter((e) => e.custom).length, [all]);

  const exercises = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (all ?? []).filter(
      (e) =>
        (ownership === 'all' || e.custom) &&
        (!group || e.muscleGroups.includes(group)) &&
        (!equipment || e.equipment === equipment) &&
        (!category || e.category === category) &&
        (!needle || e.name.toLowerCase().includes(needle)),
    );
  }, [all, ownership, group, equipment, category, q]);

  const groups = filters?.muscleGroups ?? [];

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={all ? `${all.length} exercises` : undefined}
        title="Exercise library"
        description="Find exercises to build workouts from, or add your own."
        actions={
          <Button className="shrink-0" onClick={() => setEditing('new')}><Plus className="size-4" aria-hidden /> New exercise</Button>
        }
      />

      {/* All vs this gym's own exercises */}
      <div className="mb-4 inline-flex rounded-control border border-line p-0.5">
        <OwnershipTab label="All" active={ownership === 'all'} onClick={() => setOwnership('all')} />
        <OwnershipTab label={`Custom${customCount ? ` (${customCount})` : ''}`} active={ownership === 'custom'} onClick={() => setOwnership('custom')} />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3">
        <Input
          label="Search"
          hideLabel
          leading={<Search className="size-4 text-muted" aria-hidden />}
          placeholder="Search exercises…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="flex flex-wrap gap-1.5">
          <GroupChip label="All" active={!group} onClick={() => setGroup(null)} />
          {groups.map((g) => (
            <GroupChip key={g} label={g} active={group === g} onClick={() => setGroup(g)} />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:max-w-md">
          <Select label="Equipment" hideLabel value={equipment} onChange={(e) => setEquipment(e.target.value)}>
            <option value="">Any equipment</option>
            {(filters?.equipment ?? []).map((eq) => (
              <option key={eq} value={eq}>
                {titleCase(eq)}
              </option>
            ))}
          </Select>
          <Select label="Type" hideLabel value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Any type</option>
            {(filters?.categories ?? []).map((c) => (
              <option key={c} value={c}>
                {titleCase(c)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/5] rounded-card" />
          ))}
        </div>
      ) : exercises.length === 0 ? (
        ownership === 'custom' && customCount === 0 ? (
          <EmptyState
            icon={<Dumbbell className="size-6" aria-hidden />}
            title="No custom exercises yet"
            description="Add your own exercises and they'll show up here."
            action={<Button onClick={() => setEditing('new')}><Plus className="size-4" aria-hidden /> New exercise</Button>}
          />
        ) : (
          <EmptyState icon={<Dumbbell className="size-6" aria-hidden />} title="No exercises match" description="Try clearing a filter or searching a different term." />
        )
      ) : (
        <>
          <p className="mb-2 text-sm text-muted">{exercises.length} shown</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {exercises.map((e) => (
              <ExerciseCard key={e.id} exercise={e} onView={() => setViewing(e)} onEdit={() => setEditing(e)} onDelete={() => remove(e)} />
            ))}
          </div>
        </>
      )}

      {viewing && (
        <ExerciseDetailModal
          exercise={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setEditing(viewing);
            setViewing(null);
          }}
          onDelete={() => remove(viewing)}
        />
      )}

      {editing && (
        <ExerciseFormModal
          exercise={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}

function OwnershipTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-[calc(var(--radius-control)-2px)] px-3.5 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-ink text-on-dark' : 'text-ink-soft hover:text-ink',
      )}
    >
      {label}
    </button>
  );
}

/** Read-only detail for an exercise — image, what it trains, and how to do it. */
function ExerciseDetailModal({ exercise, onClose, onEdit, onDelete }: { exercise: Exercise; onClose: () => void; onEdit: () => void; onDelete: () => void }) {
  const [broken, setBroken] = useState(false);
  const img = exercise.images[0];
  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[85vh] w-full flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-ink">{exercise.name}</h2>
            {exercise.custom && (
              <span className="mt-1 inline-block rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink">Custom</span>
            )}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
          {img && !broken ? (
            <img src={img} alt={exercise.name} loading="lazy" onError={() => setBroken(true)} className="mb-3 max-h-64 w-full rounded-control object-cover" />
          ) : (
            <div className="mb-3 flex h-40 items-center justify-center rounded-control bg-surface text-muted">
              <Dumbbell className="size-8" aria-hidden />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            {exercise.muscleGroups.map((g) => (
              <Badge key={g}>{g}</Badge>
            ))}
            {exercise.equipment && <span className="text-sm text-muted">{titleCase(exercise.equipment)}</span>}
            {exercise.category && <span className="text-sm text-muted">· {titleCase(exercise.category)}</span>}
          </div>

          {(exercise.instructions?.length ?? 0) > 0 ? (
            <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm text-ink-soft">
              {exercise.instructions.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          ) : (
            <p className="mt-3 text-sm text-muted">No description for this exercise.</p>
          )}
        </div>

        {exercise.custom && (
          <div className="mt-3 flex justify-end gap-2 border-t border-line pt-3">
            <Button variant="ghost" onClick={onDelete} className="mr-auto text-bad-ink hover:bg-bad-soft">
              <Trash2 className="size-4" aria-hidden /> Delete
            </Button>
            <Button variant="outline" onClick={onEdit}>
              <Pencil className="size-4" aria-hidden /> Edit
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ExerciseFormModal({ exercise, onClose, onSaved }: { exercise: Exercise | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(exercise?.name ?? '');
  const [equipment, setEquipment] = useState(exercise?.equipment ?? '');
  const [muscleGroups, setMuscleGroups] = useState<string[]>(exercise?.muscleGroups ?? []);
  const [instructions, setInstructions] = useState((exercise?.instructions ?? []).join('\n'));
  const [image, setImage] = useState(exercise?.images?.[0] ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (!name.trim()) return setError('Give the exercise a name.');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        equipment: equipment.trim() || null,
        muscleGroups,
        instructions: instructions.split('\n').map((s) => s.trim()).filter(Boolean),
        image: image.trim() || null,
      };
      if (exercise) await updateCustomExercise(exercise.id, body);
      else await createCustomExercise(body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the exercise.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex w-full flex-col gap-3">
        <h2 className="font-display text-lg font-semibold text-ink">{exercise ? 'Edit exercise' : 'New exercise'}</h2>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sled push" />
        <Input label="Equipment" value={equipment} onChange={(e) => setEquipment(e.target.value)} placeholder="e.g. Sled, none" />
        <MultiSelect label="Muscle groups" values={muscleGroups} onChange={setMuscleGroups} options={MUSCLE_GROUPS} allowCustom={false} placeholder="Which areas it trains" />
        <ImagePick value={image} onChange={setImage} />
        <Textarea label="Instructions" hint="One step per line" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={5} />
        {error && <p role="alert" className="text-sm font-medium text-bad-ink">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} loading={saving}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

/** Minimal image picker: choose a file, upload to Azure, keep the returned URL. */
function ImagePick({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const { url } = await uploadGymImage(f, f.name);
      onChange(url);
    } catch {
      /* ignore — user can retry */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-ink-soft">Image</p>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="size-16 rounded-control object-cover" />
        ) : (
          <span className="grid size-16 place-items-center rounded-control bg-surface text-muted"><Dumbbell className="size-6" aria-hidden /></span>
        )}
        <Button type="button" variant="outline" size="sm" loading={busy} onClick={() => fileRef.current?.click()}>
          <ImagePlus className="size-4" aria-hidden /> {value ? 'Replace' : 'Upload'}
        </Button>
        {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>Remove</Button>}
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}

function GroupChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-9 rounded-pill px-3.5 text-sm font-medium transition-colors',
        active ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft hover:bg-line',
      )}
    >
      {label}
    </button>
  );
}

function ExerciseCard({ exercise, onView, onEdit, onDelete }: { exercise: Exercise; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const [broken, setBroken] = useState(false);
  const img = exercise.images[0];
  // Edit/Delete sit over the card; stop them from also triggering the card's view click.
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  return (
    <Card className="relative flex flex-col overflow-hidden p-0 transition-shadow hover:shadow-card-hover">
      <button type="button" onClick={onView} className="flex flex-1 flex-col text-left">
        <div className="relative aspect-square bg-surface">
          {img && !broken ? (
            <img
              src={img}
              alt={exercise.name}
              loading="lazy"
              onError={() => setBroken(true)}
              className="size-full object-cover"
            />
          ) : (
            <div className="flex size-full items-center justify-center text-muted">
              <Dumbbell className="size-8" aria-hidden />
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-3">
          <p className="line-clamp-2 text-sm font-semibold text-ink">{exercise.name}</p>
          <div className="mt-auto flex flex-wrap gap-1">
            {exercise.muscleGroups.slice(0, 2).map((g) => (
              <Badge key={g}>{g}</Badge>
            ))}
            {exercise.equipment && exercise.equipment !== 'body only' && (
              <span className="text-xs text-muted">{titleCase(exercise.equipment)}</span>
            )}
          </div>
        </div>
      </button>
      {exercise.custom && (
        <>
          <span className="pointer-events-none absolute left-2 top-2 rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink">
            Custom
          </span>
          <div className="absolute right-2 top-2 z-10 flex gap-1">
            <button type="button" aria-label="Edit" onClick={stop(onEdit)} className="grid size-7 place-items-center rounded-control bg-card/90 text-ink-soft hover:text-ink">
              <Pencil className="size-3.5" aria-hidden />
            </button>
            <button type="button" aria-label="Delete" onClick={stop(onDelete)} className="grid size-7 place-items-center rounded-control bg-card/90 text-ink-soft hover:text-bad-ink">
              <Trash2 className="size-3.5" aria-hidden />
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

function titleCase(s: string) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}
